// AiConsoleSlotContext — lets the AI 導覽 page push its drawer-view
// controls (Network / FileText / PanelRight icon row) up into the
// global Header so they sit on the same row as the AI / 專業模式
// toggle on desktop.
//
// Spec docs/specs/ai-mode-operations-console.md §4.5 — the icon row
// is conceptually a peer of the mode toggle (both shape how the
// dashboard reads to the operator), so they belong in the same
// horizontal cluster on wide screens. On mobile the Header is too
// tight, so the page renders the icon row in PageHeader actions
// instead — that branch sets the slot to null.
//
// State lives in the page that owns the drawer (Topology). The
// context only carries enough metadata for Header to render the
// IconRow correctly; it does not own the state itself.

import React, { createContext, useContext, useMemo, useState } from "react";
import type { DrawerView } from "./AiConsoleIconRow.js";

export interface AiConsoleSlot {
  view: DrawerView;
  setView: (v: DrawerView) => void;
  planSupported: boolean;
  planPending: boolean;
}

interface AiConsoleSlotContextValue {
  slot: AiConsoleSlot | null;
  setSlot: (s: AiConsoleSlot | null) => void;
}

const Ctx = createContext<AiConsoleSlotContextValue>({
  slot: null,
  setSlot: () => { /* default no-op so pages outside the provider don't crash */ },
});

export function AiConsoleSlotProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [slot, setSlot] = useState<AiConsoleSlot | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAiConsoleSlot(): AiConsoleSlotContextValue {
  return useContext(Ctx);
}
